import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { ArrowLeft, TrendingUp } from 'lucide-react';

const KeyResultDetailPage = () => {
  const { objectiveId, id, krId: paramKrId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Use id or krId from params
  const krId = id || paramKrId;

  const [keyResult, setKeyResult] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatingCurrent, setUpdatingCurrent] = useState(false);
  const [newCurrentValue, setNewCurrentValue] = useState('');
  const [newNote, setNewNote] = useState('');

  useEffect(() => {
    if (krId) {
      loadKeyResult();
    }
  }, [krId]);

  const loadKeyResult = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔑 Loading KR:', krId);
      
      const [krRes, updatesRes] = await Promise.all([
        api.get(`/key-results/${krId}`),
        api.get(`/key-results/${krId}/updates`)
      ]);
      
      console.log('✅ KR data:', krRes);
      console.log('✅ Updates:', updatesRes);
      
      setKeyResult(krRes);
      setUpdates(Array.isArray(updatesRes) ? updatesRes : []);
      
    } catch (err) {
      console.error('❌ Error loading KR:', err);
      console.error('❌ Error response:', err.response);
      setError(err.error || err.response?.data?.error || 'Error al cargar el Key Result');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCurrent = async () => {
    if (!newCurrentValue || newCurrentValue === '') {
      alert('Por favor ingresa un valor');
      return;
    }

    try {
      setUpdatingCurrent(true);

      const updateData = {
        current_value: parseFloat(newCurrentValue),
        note: newNote || undefined
      };

      console.log('💾 Updating KR:', updateData);
      await api.put(`/key-results/${krId}`, updateData);

      setNewCurrentValue('');
      setNewNote('');
      await loadKeyResult();

      alert('✅ Progreso actualizado correctamente');
    } catch (err) {
      console.error('❌ Error updating:', err);
      alert(err.error || err.response?.data?.error || 'Error al actualizar el progreso');
    } finally {
      setUpdatingCurrent(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Cargando...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate(`/objetivos/${objectiveId}`)}
          className="flex items-center space-x-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Volver</span>
        </button>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <p className="text-red-800 font-semibold">Error del servidor</p>
          <p className="text-red-600 text-sm mt-2">{error}</p>
          <button
            onClick={loadKeyResult}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  if (!keyResult) {
    return (
      <div className="text-center py-12 text-gray-500">
        <p>No se encontró el Key Result</p>
      </div>
    );
  }

  const progress = parseFloat(keyResult.progress_percentage || 0);

  return (
    <div className="space-y-6">
      <button
        onClick={() => navigate(`/objetivos/${objectiveId}`)}
        className="flex items-center space-x-2 text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft className="w-4 h-4" />
        <span>Volver al Objetivo</span>
      </button>

      {/* KR Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {keyResult.description}
            </h1>
            <p className="text-sm text-gray-600">
              Responsable: {keyResult.owner_name || 'Sin asignar'}
            </p>
          </div>
          <div className="text-right">
            <div className="text-4xl font-bold text-indigo-600">
              {progress.toFixed(1)}%
            </div>
            <p className="text-sm text-gray-600 mt-1">Progreso</p>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mt-6">
          <div className="flex justify-between text-sm text-gray-600 mb-2">
            <span>Inicial: {keyResult.initial_value} {keyResult.metric_name}</span>
            <span>Actual: {keyResult.current_value} {keyResult.metric_name}</span>
            <span>Meta: {keyResult.target_value} {keyResult.metric_name}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-4">
            <div
              className="bg-indigo-600 h-4 rounded-full transition-all duration-300"
              style={{ width: `${Math.min(progress, 100)}%` }}
            />
          </div>
        </div>
      </div>

      {/* Update Current Value */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center space-x-2">
          <TrendingUp className="w-5 h-5 text-indigo-600" />
          <span>Actualizar Progreso</span>
        </h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nuevo valor actual ({keyResult.metric_name})
            </label>
            <input
              type="number"
              value={newCurrentValue}
              onChange={(e) => setNewCurrentValue(e.target.value)}
              placeholder={`Actual: ${keyResult.current_value}`}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
              step="0.01"
            />
            <p className="text-xs text-gray-500 mt-1">
              Rango: {keyResult.initial_value} - {keyResult.target_value}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nota de actualización (opcional)
            </label>
            <textarea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Agrega contexto sobre esta actualización..."
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
              rows={3}
            />
          </div>

          <button
            onClick={handleUpdateCurrent}
            disabled={updatingCurrent || !newCurrentValue}
            className="w-full bg-indigo-600 text-white py-3 rounded-md hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-semibold"
          >
            {updatingCurrent ? 'Actualizando...' : 'Actualizar Progreso'}
          </button>
        </div>
      </div>

      {/* Updates History */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Historial de Actualizaciones</h3>
        
        {updates.length > 0 ? (
          <div className="space-y-4">
            {updates.map((update, index) => (
              <div key={index} className="border-l-4 border-indigo-600 pl-4 py-2">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-semibold text-gray-900">
                      Valor: {update.value} {keyResult.metric_name}
                    </p>
                    {update.note && (
                      <p className="text-sm text-gray-600 mt-1">{update.note}</p>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">
                    {new Date(update.created_at).toLocaleDateString('es-CL')}
                  </p>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Por: {update.user_name}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-8">
            No hay actualizaciones aún
          </p>
        )}
      </div>
    </div>
  );
};

export default KeyResultDetailPage;
